import { assign, fromPromise, sendTo, setup } from 'xstate';
import { showListingErrorAction } from '../../../listingConflicts';
import { submitCoverage } from './api';
import type { Context, Events, OnLoadEvent } from './types';

const parentOf = ({ context }: { context: Context }) => {
  if (!context.parentRef) throw new Error('parentRef is required in coverageMachine');
  return context.parentRef;
};

// [MCV-1889] Saves a selling listing's coverage step: states, coverage point and roster. It reports
// what it wrote to the listings machine and moves the wizard on only once every write has landed.
export const coverageMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actors: {
    submit: fromPromise(submitCoverage),
  },
  actions: {
    // FEATURE_DISABLED, COVERAGE_INVALID and OFFERING_ONLY are spelled out; the rest is the shared alert.
    sendError: showListingErrorAction,
    setLoading: assign({ loading: true }),
    resetLoading: assign({ loading: false }),
    setListingId: assign({ listingId: ({ event }) => (event as OnLoadEvent).payload.listingId }),
    sendCoverageToParent: sendTo(parentOf, ({ event }) => ({
      type: 'ON_UPDATE_COVERAGE',
      payload: (event as unknown as { output: unknown }).output,
    })),
    // A save that failed part-way left the writes before it on the server; the listing is told so,
    // or it would show (and the review step would count) coverage that is no longer there.
    sendSavedCoverageToParent: sendTo(parentOf, ({ event }) => ({
      type: 'ON_UPDATE_COVERAGE',
      payload: (event as unknown as { error: { savedCoverage: object } }).error.savedCoverage,
    })),
    sendToNextPage: sendTo(parentOf, () => ({ type: 'ON_NEXT' })),
  },
}).createMachine({
  context: ({ input }) => ({ listingId: -1, loading: false, parentRef: input?.parentRef }),
  initial: '$_VIEW',
  on: {
    ON_LOAD: { actions: 'setListingId' },
    // The wizard left this step while a save was in flight (the modal was closed, maybe for another
    // listing). Leaving $_SEND_SUBMIT drops the late result, so it cannot overwrite the next
    // listing's coverage or advance its wizard.
    ON_RESET: { target: '.$_VIEW', actions: 'resetLoading' },
  },
  states: {
    $_VIEW: {
      on: { ON_SUBMIT: { target: '$_SEND_SUBMIT', actions: 'setLoading' } },
    },
    $_SEND_SUBMIT: {
      invoke: {
        id: 'submitCoverage',
        src: 'submit',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: ['sendCoverageToParent', 'resetLoading', 'sendToNextPage'],
        },
        onError: [
          {
            guard: ({ event }) =>
              Object.keys((event.error as { savedCoverage?: object })?.savedCoverage ?? {}).length >
              0,
            target: '$_VIEW',
            actions: ['sendSavedCoverageToParent', 'sendError', 'resetLoading'],
          },
          { target: '$_VIEW', actions: ['sendError', 'resetLoading'] },
        ],
      },
    },
  },
});
